
import { useState } from 'react';
import { supabase } from '../../lib/supabase';

interface SuggestionBoxProps {
  className?: string;
}

export function SuggestionBox({ className = '' }: SuggestionBoxProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [suggestion, setSuggestion] = useState('');
  const [category, setCategory] = useState('General');
  const [priority, setPriority] = useState('Medium');
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const categories = [
    'General',
    'Workplace Environment',
    'Training & Development',
    'Communication',
    'Technology & Tools',
    'Processes & Procedures',
    'Benefits & Compensation',
    'Management',
    'Other'
  ];

  const priorities = [
    'Low',
    'Medium',
    'High',
    'Critical'
  ];

  const handleSubmit = async () => {
    if (!suggestion.trim()) {
      alert('Please enter your suggestion before submitting.');
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(
        `${import.meta.env.VITE_PUBLIC_SUPABASE_URL}/functions/v1/suggestion-box`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${import.meta.env.VITE_PUBLIC_SUPABASE_ANON_KEY}`,
          },
          body: JSON.stringify({
            action: 'submit_suggestion',
            suggestion: suggestion.trim(),
            category,
            priority
          }),
        }
      );

      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }

      const data = await response.json();
      console.log('Suggestion response:', data);

      if (data.success) {
        setSubmitted(true);
        setSuggestion('');
        setCategory('General');
        setPriority('Medium');
        setTimeout(() => {
          setSubmitted(false);
          setIsOpen(false);
        }, 3000);
      } else {
        const errorMessage = data.error || 'Unknown error occurred';
        console.error('Suggestion submission failed:', errorMessage);
        alert(`Failed to submit suggestion: ${errorMessage}`);
      }
    } catch (error) {
      console.error('Error submitting suggestion:', error);
      alert(`An error occurred while submitting your suggestion: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    setSuggestion('');
    setCategory('General');
    setPriority('Medium');
    setSubmitted(false);
  };

  return (
    <div className={className}>
      {/* Suggestion Box Button */}
      <button
        onClick={() => setIsOpen(true)}
        className="w-full bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl shadow-lg border-2 border-blue-200 p-4 hover:shadow-xl hover:-translate-y-1 transition-all duration-200 hover:from-blue-600 hover:to-purple-700"
      >
        <div className="flex items-center justify-center space-x-3">
          <div className="w-10 h-10 bg-white bg-opacity-20 rounded-full flex items-center justify-center">
            <i className="ri-lightbulb-line text-xl"></i>
          </div>
          <div className="text-left">
            <h3 className="font-semibold text-lg">Anonymous Suggestion Box</h3>
            <p className="text-blue-100 text-sm">Share your ideas to improve our workplace</p>
          </div>
          <i className="ri-arrow-right-circle-line text-2xl text-blue-200"></i>
        </div>
      </button>

      {/* Modal */}
      {isOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            {/* Header */}
            <div className="px-6 py-4 border-b border-gray-200 bg-gradient-to-r from-blue-50 to-purple-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-blue-100 rounded-full flex items-center justify-center">
                    <i className="ri-lightbulb-line text-blue-600 text-xl"></i>
                  </div>
                  <div>
                    <h2 className="text-xl font-bold text-gray-900">Anonymous Suggestion Box</h2>
                    <p className="text-sm text-gray-600">Your feedback helps us improve</p>
                  </div>
                </div>
                <button
                  onClick={handleClose}
                  className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center hover:bg-gray-200 transition-colors"
                >
                  <i className="ri-close-line text-gray-600"></i>
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="px-6 py-6">
              {submitted ? (
                <div className="text-center py-8">
                  <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                    <i className="ri-check-line text-green-600 text-2xl"></i>
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 mb-2">Thank You!</h3>
                  <p className="text-gray-600 text-sm">
                    Your suggestion has been submitted anonymously. We appreciate your feedback and will review it carefully.
                  </p>
                </div>
              ) : (
                <div className="space-y-4">
                  {/* Anonymous Notice */}
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-start space-x-3">
                      <i className="ri-shield-check-line text-blue-600 text-lg mt-0.5"></i>
                      <div>
                        <h4 className="font-medium text-blue-900 text-sm">100% Anonymous</h4>
                        <p className="text-blue-700 text-xs mt-1">
                          Your identity will not be recorded or shared. Only administrators can view suggestions.
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Category Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Category
                    </label>
                    <select
                      value={category}
                      onChange={(e) => setCategory(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      {categories.map((cat) => (
                        <option key={cat} value={cat}>
                          {cat}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Priority Selection */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Priority Level
                    </label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                    >
                      {priorities.map((pri) => (
                        <option key={pri} value={pri}>
                          {pri}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Suggestion Text */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Your Suggestion
                    </label>
                    <textarea
                      value={suggestion}
                      onChange={(e) => setSuggestion(e.target.value)}
                      placeholder="Share your ideas, concerns, or suggestions for improving our workplace..."
                      rows={6}
                      maxLength={1000}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm resize-none"
                    />
                    <div className="flex justify-between items-center mt-1">
                      <p className="text-xs text-gray-500">
                        Be specific and constructive in your feedback
                      </p>
                      <span className="text-xs text-gray-400">
                        {suggestion.length}/1000
                      </span>
                    </div>
                  </div>

                  {/* Submit Button */}
                  <div className="flex space-x-3 pt-4">
                    <button
                      onClick={handleClose}
                      className="flex-1 px-4 py-2 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-colors text-sm"
                    >
                      Cancel
                    </button>
                    <button
                      onClick={handleSubmit}
                      disabled={loading || !suggestion.trim()}
                      className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors text-sm flex items-center justify-center"
                    >
                      {loading ? (
                        <>
                          <i className="ri-loader-4-line animate-spin mr-2"></i>
                          Submitting...
                        </>
                      ) : (
                        <>
                          <i className="ri-send-plane-line mr-2"></i>
                          Submit Anonymously
                        </>
                      )}
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SuggestionBox;
